// PromptGenerator/hooks/useProjectData.ts
import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../../../config/api';
import { useAuth } from '../../../context/Auth/AuthContext';
import { generateTitleFromPrompt } from '../../../services/projectService'; // Assuming this is the path
import { PromptProject } from '../../../services/projectService'; // Assuming this interface is available

export interface PromptData {
  id: string;
  projectId: string;
  title?: string;
  generatedPrompt: string;
  userSelections: {
    userIdea: string;
    attributes: string;
  };
  promptMetadata?: {
    characterId?: string;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

interface Project extends PromptProject {
  // Extend or define Project interface based on use
}

export const useProjectData = (projectId: string | undefined) => {
  const { token } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [prompts, setPrompts] = useState<PromptData[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProject = useCallback(async () => {
    if (!projectId || !token) return;
    try {
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (result) {
        setProject(result);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    }
  }, [projectId, token]);

  const loadPrompts = useCallback(async () => {
    if (!projectId || !token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/prompt-generator/prompts/${projectId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      
      if (result.success) {
        const promptsWithTitles = result.data.map((prompt: PromptData) => ({
          ...prompt,
          // Ensure prompt has a title for display
          title: prompt.title || generateTitleFromPrompt(prompt.userSelections?.userIdea || 'Untitled Prompt', 8)
        }));
        setPrompts(promptsWithTitles);
      } else {
        setPrompts([]);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    if (projectId && token) {
      loadProject();
      loadPrompts();
    }
  }, [projectId, token, loadProject, loadPrompts]);

  return { project, prompts, loading, setProject, setPrompts, loadPrompts };
};